import React, { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CONCERNS = [
  "Couples Therapy",
  "Family Therapy",
  "Individual Therapy",
  "Premarital Counseling",
  "Grief & Trauma",
  "Not sure yet",
];

const initial = {
  name: "",
  email: "",
  phone: "",
  concern: "",
  preferred_time: "",
  message: "",
};

const ease = [0.16, 1, 0.3, 1];

export default function ConsultationForm() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please share your name, email, and phone.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.concern) delete payload.concern;
      if (!payload.preferred_time) delete payload.preferred_time;
      if (!payload.message) delete payload.message;

      await axios.post(`${API}/consultations`, payload);
      toast.success("Received. I'll be in touch personally soon.");
      setDone(true);
      setForm(initial);
    } catch (err) {
      const data = err?.response?.data;
      let msg = "Something went wrong. Please try again or call 209.670.6799.";

      if (data) {
        const d = data.detail;
        if (typeof d === "string") {
          msg = d;
        } else if (Array.isArray(d) && d.length) {
          // Pydantic 422 — surface the first validation issue in plain English
          const first = d[0];
          const field = Array.isArray(first?.loc)
            ? first.loc[first.loc.length - 1]
            : "field";
          const raw = first?.msg || "Invalid value";
          const pretty = raw.replace(/^Value error, ?/, "");
          const label =
            field === "email"
              ? "Email"
              : field === "phone"
                ? "Phone"
                : field === "name"
                  ? "Name"
                  : String(field);
          msg = `${label}: ${pretty}`;
        }
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="book"
      data-testid="consultation-section"
      className="relative w-full py-28 sm:py-40 bg-[#EFECE5] border-t border-[#D5D1C9]"
    >
      <div className="mx-auto max-w-[1440px] px-6 sm:px-10">
        <div className="flex items-baseline justify-between border-b border-[#D5D1C9] pb-6">
          <span className="font-sans-ui text-[11px] uppercase tracking-[0.3em] text-[#5C5C59]">
            (Ch. IV) — Begin
          </span>
          <span className="font-serif-display italic text-[#5C5C59]">consultation</span>
        </div>

        <div className="mt-16 grid grid-cols-12 gap-10 lg:gap-16">
          <div className="col-span-12 lg:col-span-5">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.9, ease }}
              className="font-serif-display font-light tracking-tighter leading-[1] text-5xl sm:text-6xl lg:text-7xl"
            >
              Request a <em className="italic text-[#2C4C3B]">free</em> 15-min consultation.
            </motion.h2>
            <p className="mt-8 max-w-[42ch] font-sans-ui text-[15px] leading-relaxed text-[#5C5C59]">
              A short, no-pressure call to hear what’s bringing you here and see if we’re the
              right fit. You will reach me — not an assistant. Confidential by default.
            </p>

            <ul className="mt-10 space-y-3 font-sans-ui text-[13px] text-[#1A1A18]">
              <li className="flex items-center gap-3">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#2C4C3B]" />
                California residents (telehealth only)
              </li>
              <li className="flex items-center gap-3">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#2C4C3B]" />
                Individuals, couples & families welcomed
              </li>
              <li className="flex items-center gap-3">
                <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#2C4C3B]" />
                A personal reply — not an automated one
              </li>
            </ul>

            <div className="mt-12 border-t border-[#D5D1C9] pt-6 flex items-center justify-between">
              <a
                href="tel:2096706799"
                data-testid="form-side-phone"
                className="link-under font-sans-ui text-[13px] uppercase tracking-[0.22em] text-[#1A1A18]"
              >
                Or call — 209.670.6799
              </a>
              <span className="font-serif-display italic text-[#2C4C3B]">— warmly, dlg</span>
            </div>
          </div>

          {/* Form */}
          <form
            data-testid="consultation-form"
            onSubmit={onSubmit}
            className="col-span-12 lg:col-span-7 lg:col-start-6"
          >
            {done ? (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease }}
                data-testid="consultation-success"
                className="border border-[#D5D1C9] bg-[#F7F5F0] p-10"
              >
                <span className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-[#2C4C3B]">
                  ✓ Received
                </span>
                <h3 className="mt-4 font-serif-display text-4xl leading-tight text-[#1A1A18]">
                  Thank you.
                </h3>
                <p className="mt-4 font-sans-ui text-[15px] text-[#5C5C59] leading-relaxed">
                  Your note is with me. I&apos;ll be in touch personally to set up a short
                  consultation call — please allow a few days for a thoughtful response. If
                  it&apos;s urgent in the meantime, feel free to call{" "}
                  <a href="tel:2096706799" className="link-under text-[#1A1A18]">
                    209.670.6799
                  </a>
                  .
                </p>
                <button
                  type="button"
                  data-testid="consultation-reset"
                  onClick={() => setDone(false)}
                  className="mt-8 font-sans-ui text-[11px] uppercase tracking-[0.28em] text-[#1A1A18] link-under"
                >
                  Send another →
                </button>
              </motion.div>
            ) : (
              <div className="grid grid-cols-2 gap-x-8 gap-y-2">
                <Field label="Your name" required span={2} sm={1}>
                  <input
                    data-testid="input-name"
                    name="name"
                    autoComplete="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="First & last"
                    className="editorial-input"
                    required
                  />
                </Field>
                <Field label="Email" required span={2} sm={1}>
                  <input
                    data-testid="input-email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@domain.com"
                    className="editorial-input"
                    required
                  />
                </Field>
                <Field label="Phone" required span={2} sm={1}>
                  <input
                    data-testid="input-phone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    value={form.phone}
                    onChange={onChange}
                    placeholder="(555) 555-5555"
                    className="editorial-input"
                    required
                  />
                </Field>
                <Field label="I'd like help with" span={2} sm={1}>
                  <select
                    data-testid="input-concern"
                    name="concern"
                    value={form.concern}
                    onChange={onChange}
                    className="editorial-input appearance-none pr-6"
                  >
                    <option value="">— select one —</option>
                    {CONCERNS.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Best time to reach you" span={2}>
                  <input
                    data-testid="input-time"
                    name="preferred_time"
                    value={form.preferred_time}
                    onChange={onChange}
                    placeholder="e.g. Weekday evenings PT"
                    className="editorial-input"
                  />
                </Field>
                <Field label="Anything I should know" span={2}>
                  <textarea
                    data-testid="input-message"
                    name="message"
                    rows={3}
                    value={form.message}
                    onChange={onChange}
                    placeholder="A short note (optional)"
                    className="editorial-input"
                  />
                </Field>

                <div className="col-span-2 mt-10 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-6">
                  <p className="font-sans-ui text-[11px] text-[#5C5C59] max-w-[42ch] leading-relaxed">
                    By submitting, you agree to be contacted about scheduling. This form is not
                    for emergencies. If you are in crisis, please call or text{" "}
                    <span className="text-[#1A1A18]">988</span>.
                  </p>
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="consultation-submit"
                    className="group inline-flex items-center gap-3 rounded-full bg-[#2C4C3B] px-7 py-4 text-[12px] uppercase tracking-[0.24em] text-[#F7F5F0] hover:bg-[#1E382A] active:scale-[0.98] disabled:opacity-60 transition-[background-color,transform] duration-200"
                  >
                    {submitting ? "Sending…" : "Send request"}
                    <span className="inline-block h-6 w-6 border border-[#F7F5F0]/40 rounded-full grid place-items-center transition-transform group-hover:translate-x-1">
                      →
                    </span>
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}

function Field({ label, required, children, span = 2, sm }) {
  const spanCls =
    span === 2 ? "col-span-2" : span === 1 ? "col-span-1" : "col-span-2";
  const smCls = sm === 1 ? "sm:col-span-1" : sm === 2 ? "sm:col-span-2" : "";
  return (
    <label className={`${spanCls} ${smCls} block pt-6`}>
      <span className="font-sans-ui text-[10px] uppercase tracking-[0.3em] text-[#5C5C59]">
        {label} {required && <span className="text-[#2C4C3B]">*</span>}
      </span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
